/**
 * Convert a string to sentence case
 * @param {string} str - string to convert
 * @returns {string} - converted string
 * @example toSentenceCase('Hello World') => 'Hello world'
 * @example toSentenceCase('hello world') => 'Hello world'
 */
export function toSentenceCase(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Convert a string to lower case
 * @param {string} str - string to convert
 * @returns {string} - converted string
 * @example toLowerCase('Hello') => 'hello'
 */
export function toLowerCase(str: string): string {
  return str.toLowerCase();
}

/**
 * Convert a given number to a star unicode character string
 * @param {number} value - number of stars
 * @returns {string} - unicode string
 * @example toStars(3) => '★★★'
 */

export function toStars(value: number): string {
  const stars = '★';
  return stars.repeat(value);
}

/**
 * Replaces placeholders in the template string with corresponding values from the provided object.
 *
 * @param template - The template string containing placeholders in the format ${key}.
 * @param values - An object containing key-value pairs where the key corresponds to the placeholder in the template.
 * @returns The template string with placeholders replaced by corresponding values from the object.
 * @example
 * const template = "Hello, ${name}! You have ${count} new messages.";
 * const values = { name: "Alice", count: "5" };
 * const result = replacePlaceholders(template, values);
 * console.log(result); // "Hello, Alice! You have 5 new messages."
 */
export function replacePlaceholders(
  template: string,
  values: { [key: string]: string },
): string {
  return template.replace(/\${(.*?)}/g, (match, p1) => {
    const value = values[p1];
    return value !== undefined ? value : match;
  });
}

/**
 * Ensures that the provided content is wrapped in a <p> tag.
 *
 * This function checks if the input string contains a <p> tag. If it does not,
 * it wraps the entire content in a <p> tag and returns the modified string.
 * If the content already contains a <p> tag, it returns the original string.
 *
 * @param content - The string content to be checked and potentially wrapped in a <p> tag.
 * @returns The content wrapped in a <p> tag if it was not already, otherwise the original content.
 */
export function ensureParagraphTag(content: string): string {
  const paragraphTagRegex = /<p\b[^>]*>(.*?)<\/p>/i;
  if (!paragraphTagRegex.test(content)) {
    return `<p>${content}</p>`;
  }
  return content;
}
